import { ReactNode } from 'react';
import { cx } from './cx';
import { Text, typography } from './Text';

export interface PageHeaderProps {
  title: string;
  /** Rendered inline right after the title — e.g. a favorite star. */
  titleAdornment?: ReactNode;
  description?: ReactNode;
  /** A lucide icon (~22px) shown in the featured-icon box. */
  icon?: ReactNode;
  /** Small label/breadcrumb row above the title. */
  eyebrow?: ReactNode;
  /** Right-aligned controls — typically a RefreshButton and/or actions. */
  actions?: ReactNode;
  /** A metadata row under the title block — e.g. owner, lifecycle and tags on
   * an entity page. */
  meta?: ReactNode;
  /** Rendered under the title block — typically a Tabs bar. */
  children?: ReactNode;
  className?: string;
}

/**
 * The Regask page header — Untitled UI "page header" pattern: a featured icon,
 * title and supporting text, an actions slot on the right, and an optional tab
 * bar beneath. Flat and clean (no gradient hero, no bottom rule); every
 * top-level page opens with one.
 */
export const PageHeader = ({
  title,
  titleAdornment,
  description,
  icon,
  eyebrow,
  actions,
  meta,
  children,
  className,
}: PageHeaderProps) => (
  <header className={cx('mb-10', className)}>
    {eyebrow && (
      <div
        className={cx(
          typography.body,
          'mb-4 flex items-center gap-2 text-fg-tertiary',
        )}
      >
        {eyebrow}
      </div>
    )}
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-3.5">
          {icon && (
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-default bg-surface text-brand-text shadow-xs">
              {icon}
            </span>
          )}
          <Text variant="display" as="h1">
            {title}
          </Text>
          {titleAdornment}
        </div>
        {description && (
          <Text
            variant="subtitle"
            tone="tertiary"
            className="mt-2 max-w-[720px]"
          >
            {description}
          </Text>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          {actions}
        </div>
      )}
    </div>
    {meta && <div className="mt-5">{meta}</div>}
    {children && <div className="mt-5">{children}</div>}
  </header>
);
